import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Column,
    useTable,
    useBlockLayout,
    HeaderGroup,
    useGlobalFilter,
    useSortBy,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import * as naturalCompare from 'string-natural-compare';
import _omit from 'lodash/omit';

import {
    DetectionsCell,
    DollarsCellCompact,
    IconInfoCircle,
    leftNavConstants,
    PlaysCell,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TruncatedListCell,
} from '@trqk/components';
import {
    constants,
    createFuzzyTextFilter,
    detectionIsOn,
    getTitleFilenameSortKey,
    getScaledColumnWidths,
    getScrollbarWidth,
    locale,
} from '@trqk/utils';
import { useAutoSize, useDebounce } from '@trqk/hooks';

import {
    // AudioStatusCell,
    GlobalFilter,
    TitleCell,
    PercentSharesCell,
    RefreshStatus,
} from './components';
import {
    columnWidthInputs,
    rowHeight,
    minWidthOffset,
    defaultColumnWidth,
} from './constants';
import {
    useEntities,
    useUploadStatements,
    usePaymentPlanStatus,
} from '../../../../../hooks';

const { SEARCH_FILTER_TYPES } = constants;
const { AUTO_SIZE_OFFSETS, HEIGHTS, WIDTHS } = leftNavConstants;

type WorksTableColumn = Column<WorksTableRow> & {
    headerLabel: string;
    globalFilter?: string;
    updateMyData?(itemId: string, columnId: string, value: unknown): void;
    trackDetails?: WorkDetail[];
};

interface WorksTableProps {
    header: JSX.Element;
    /**
     * Current entity name
     */
    name: Maybe<string>;
    /**
     * Called when user scrolls to the bottom of the list
     */
    onScrollToBottom(): void;
    /**
     * Called when user types a value into the search field; call frequency is debounced
     */
    onSearch(searchTerm?: string): void;
    /**
     * Re-fetch the `trqkEntity` to refresh the initial list of tracks
     */
    refetch?(): void;
    /**
     * A timestamp that can be changed to trigger an update of the table data
     */
    refreshTime: number;
    /**
     * Current entity's role type
     */
    roleType: ProRoleType | null;
    /**
     * Array of {@link WorksTableRow} objects to render as table rows
     */
    tracks: WorksTableRow[];
    /**
     * Current user ID
     */
    userId: Maybe<string>;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of the user's statement works. The parent `Works` component handles selection of
 * the current type of work being displayed, i.e. 'All', 'Tracks' or 'Cue Sheets.'
 * @param props
 * @returns
 */
export const WorksTable = ({
    header,
    name,
    onScrollToBottom,
    onSearch,
    refetch,
    refreshTime,
    roleType,
    tracks,
    userId,
}: WorksTableProps): JSX.Element => {
    const [data, setData] = useState(tracks);

    // When our cell renderer calls updateMyData, use the item id, columnId and new value to update
    // the original data
    const skipPageResetRef = useRef(false);

    useEffect(() => {
        if (refreshTime > 0) {
            skipPageResetRef.current = true;

            setData(tracks);
        }
    }, [refreshTime, tracks]);

    const deleteRow = useCallback(
        (itemId): void => {
            skipPageResetRef.current = true;

            setData(prevData => prevData.filter(row => row.id !== itemId));

            if (refetch) {
                // Calling refetch() will not cause the refreshTime to change, which is what we
                // want, so that the table row is deleted without re-rendering the whole table.
                refetch();
            }
        },
        [setData, skipPageResetRef, refetch],
    );

    const updateMyData: TableDataUpdater = useCallback(
        (itemId, columnId, value) => {
            skipPageResetRef.current = true;

            setData(prevData =>
                prevData.map(row => {
                    if (row.id === itemId && columnId !== undefined) {
                        return {
                            ...row,
                            [columnId]: value,
                        };
                    }

                    return row;
                }),
            );
        },
        [setData, skipPageResetRef],
    );

    const totalDetections = useMemo(
        (): number =>
            data.reduce(
                (acc: number, { detection_status }: WorksTableRow): number => {
                    if (detectionIsOn(detection_status)) {
                        return acc + 1;
                    }

                    return acc;
                },
                0,
            ),
        [data],
    );

    useEffect(() => {
        skipPageResetRef.current = false;
    });

    // Calculate table height based on the available space in the viewport. The table will grow to
    // fill the viewport, but it will not cause the page to scroll.
    const tableElementRef = useRef<HTMLDivElement>(null);
    const { isPublisherEdition, showFreeTrialFooter } = usePaymentPlanStatus();
    const { height, width } = useAutoSize({
        elementRef: tableElementRef,
        resizeRate: 400,
        rightOffset: AUTO_SIZE_OFFSETS.RIGHT,
        bottomOffset: showFreeTrialFooter
            ? HEIGHTS.ACCOUNT_DROPDOWN + AUTO_SIZE_OFFSETS.BOTTOM
            : AUTO_SIZE_OFFSETS.BOTTOM,
        minWidth: WIDTHS.MAIN_XL - minWidthOffset,
    });

    /* BEGIN table management logic */

    const trackDetails = useMemo(
        () =>
            data.reduce(
                (acc: WorkDetail[], track: WorksTableRow): WorkDetail[] => {
                    const { title, id, performance_count, earnings } = track;
                    const hasEarnings =
                        typeof earnings === 'number' && earnings > 0;
                    const hasPerformanceCount = performance_count !== null;
                    const isUnmatchedTrack =
                        !hasEarnings && !hasPerformanceCount;

                    if (
                        title !== null &&
                        id !== null &&
                        // Do not show unmatched tracks in InviteCollaborators modal triggered from
                        // Writers or Publishers columns
                        !isUnmatchedTrack
                    ) {
                        acc.push({ title, id });
                    }

                    return acc;
                },
                [] as WorkDetail[],
            ),
        [data],
    );

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(() => {
        // Remove Detections column from scaling when on Publisher Edition
        const scaledColumnWidths = isPublisherEdition
            ? getScaledColumnWidths(
                  width,
                  _omit(columnWidthInputs, ['detections']),
              )
            : getScaledColumnWidths(width, columnWidthInputs);

        const columnList: WorksTableColumn[] = [
            {
                id: 'track',
                Header: TableHeader,
                headerLabel: 'Work Title / Audio',
                accessor: ({
                    audio_id,
                    filename,
                    title,
                    song_id,
                    uploading_filename,
                    progress,
                }: WorksTableRow): {
                    audio_id: Maybe<string>;
                    filename: Maybe<string>;
                    title: Maybe<string>;
                    song_id: Maybe<string>;
                    progress: Maybe<number>;
                    uploading_filename: Maybe<string>;
                } => ({
                    audio_id,
                    filename,
                    title,
                    song_id,
                    progress,
                    uploading_filename,
                }),
                Cell: TitleCell,
                width: scaledColumnWidths?.track ?? defaultColumnWidth,
                globalFilter: SEARCH_FILTER_TYPES.TITLE_FILTER,
                sortType: (
                    rowA: Row<WorksTableRow>,
                    rowB: Row<WorksTableRow>,
                ): number => {
                    const sortKeyA = getTitleFilenameSortKey({
                        title: rowA.original.title,
                        filename: rowA.original.filename,
                    });
                    const sortKeyB = getTitleFilenameSortKey({
                        title: rowB.original.title,
                        filename: rowB.original.filename,
                    });

                    return naturalCompare(sortKeyA, sortKeyB);
                },
                updateMyData,
            },
            {
                id: 'writers',
                Header: TableHeader,
                headerLabel: 'Writers',
                width: scaledColumnWidths?.writers ?? defaultColumnWidth,
                accessor: ({
                    percent_share,
                    song_id,
                    title,
                }: WorksTableRow): PercentSharesCellValue => ({
                    name,
                    percent_share: roleType === 'writer' ? percent_share : 0,
                    roleType: 'writer' as ProRoleType,
                    title,
                    totalShares: 50,
                    trqkSongId: song_id,
                }),
                trackDetails,
                Cell: PercentSharesCell,
            },
            {
                id: 'publishers',
                Header: TableHeader,
                headerLabel: 'Publishers',
                width: scaledColumnWidths?.publishers ?? defaultColumnWidth,
                accessor: ({
                    percent_share,
                    song_id,
                    title,
                }: WorksTableRow): PercentSharesCellValue => ({
                    name,
                    percent_share: roleType === 'publisher' ? percent_share : 0,
                    roleType: 'publisher' as ProRoleType,
                    title,
                    totalShares: 50,
                    trqkSongId: song_id,
                }),
                trackDetails,
                Cell: PercentSharesCell,
            },
            {
                id: 'proId',
                Header: TableHeader,
                headerLabel: 'PRO ID',
                accessor: ({ work_ids }: WorksTableRow): Maybe<string[]> =>
                    work_ids,
                Cell: TruncatedListCell,
                width: scaledColumnWidths?.proId ?? defaultColumnWidth,
            },
            {
                id: 'plays',
                Header: TableHeader,
                headerLabel: 'Plays',
                accessor: ({
                    performance_count,
                }: WorksTableRow): Maybe<number> => performance_count ?? null,
                Cell: PlaysCell,
                width: scaledColumnWidths?.plays ?? defaultColumnWidth,
            },
            {
                id: 'earnings',
                Header: TableHeader,
                headerLabel: 'Earnings',
                accessor: ({ earnings }: WorksTableRow): Maybe<number> =>
                    earnings,
                Cell: DollarsCellCompact,
                width: scaledColumnWidths?.earnings ?? defaultColumnWidth,
            },
        ];

        // Only add Detections column when on Indie Edition
        if (!isPublisherEdition) {
            columnList.push({
                id: 'detections',
                Header: TableHeader,
                headerLabel: 'Detections',
                accessor: ({
                    detection_status,
                }: WorksTableRow): Maybe<string> => detection_status,
                Cell: DetectionsCell,
                width: scaledColumnWidths?.detections ?? defaultColumnWidth,
            });
        }

        return columnList;
    }, [
        deleteRow,
        isPublisherEdition,
        name,
        roleType,
        totalDetections,
        trackDetails,
        updateMyData,
        userId,
        width,
    ]);

    const filterTypes = useMemo(
        () => ({
            titleFilter: createFuzzyTextFilter({
                keys: ['values.track.title'],
            }),
        }),
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
        state,
        setGlobalFilter,
        // setSortBy,
        // https://app.asana.com/0/1150917131612043/1169457575371092
        // For some reason, setSortBy is undefined if we uncomment this, although the docs say it
        // should be on the table instance. If we can get access to setSortBy, we should be able to
        // call setSortBy([]) in the TrackNameInput so that the rows are no longer sorted when the
        // title is changed.
    } = useTable<WorksTableRow>(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            initialState: {
                sortBy: [
                    {
                        id: 'track',
                        desc: false,
                    },
                ],
            },
            globalFilter: filterTypes.titleFilter,
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetGlobalFilter: false,
            autoResetRowState: !skipPageResetRef.current,
        },
        useBlockLayout,
        useGlobalFilter,
        useSortBy,
    );

    /* END table management logic */

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<WorksTableRow>
                    index={index}
                    style={style}
                    row={row}
                />
            );
        },
        [prepareRow, rows],
    );

    const { selectedEntityId } = useEntities();
    const { entityRefreshState } = useUploadStatements();
    const entityRefreshStatus =
        selectedEntityId === null
            ? null
            : entityRefreshState?.[selectedEntityId] ?? null;

    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.onscroll = (e: Event): void => {
                const target = e.target as HTMLDivElement;

                if (target) {
                    const atBottom =
                        target.scrollHeight - target.scrollTop ===
                        target.clientHeight;

                    if (atBottom) {
                        onScrollToBottom();
                    }
                }
            };
        }
    }, [listRef.current, onScrollToBottom]);

    const debouncedSearchTerm = useDebounce(state.globalFilter, 500);

    useEffect(() => {
        onSearch(debouncedSearchTerm);
    }, [debouncedSearchTerm, onSearch]);

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <>
            <div
                className="grid mt-2"
                data-cy="works-table"
                style={{
                    gridTemplateColumns: '400px auto',
                }}
            >
                <div>
                    <GlobalFilter
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                    />
                </div>
                <div className="flex flex-col items-end pr-2">{header}</div>
            </div>
            <div className="flex justify-end mt-1">
                {entityRefreshStatus !== null && (
                    <RefreshStatus entityRefreshStatus={entityRefreshStatus} />
                )}
            </div>
            <div
                ref={tableElementRef}
                {...getTableProps()}
                className="mt-6 trqk-table text-sm font-normal text-tertiary-400"
            >
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<WorksTableRow>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<WorksTableRow>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div {...getTableBodyProps()}>
                    {rows.length === 0 && (
                        <div
                            className="flex justify-center items-center italic text-tertiary-400"
                            style={{ height: 350 }}
                        >
                            <div className="flex flex-col items-center justify-center">
                                <div className="flex items-center">
                                    <IconInfoCircle
                                        addClasses="mr-1"
                                        width={20}
                                        fillColor="primary-500"
                                    />
                                    {locale.enUS.WORKS.CURRENT_DATA_EMPTY}
                                </div>
                            </div>
                        </div>
                    )}
                    {rows.length > 0 && (
                        <FixedSizeList
                            height={height}
                            itemCount={rows.length}
                            itemSize={rowHeight}
                            width={totalColumnsWidth + scrollbarWidth}
                            outerRef={listRef}
                        >
                            {RenderRow}
                        </FixedSizeList>
                    )}
                </div>
            </div>
        </>
    );
};
